// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import prepareHeaders from 'services/utils/prepare-headers';
import { CaseType, CaseTypeDetails, ReferenceItem, RiskAssessmentDeadline, ValidationDeadline } from './types';

// Define a service using a base URL and expected endpoints
export const caseTypeApi = createApi({
  reducerPath: 'caseTypeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: 'api/casetype',
    prepareHeaders,
  }),

  tagTypes: ['Casetypes'],
  endpoints: (builder) => ({
    getCaseTypeOverview: builder.query<Array<CaseType>, string>({
      query: () => `getOverview`,
      transformResponse: (rawResult: { data: Array<CaseType> }) =>
        rawResult.data,
      keepUnusedDataFor: 0,
      providesTags: ['Casetypes'],
    }),
    getCaseTypeDetails: builder.query<CaseTypeDetails, string>({
      query: (id) => `getDetails?id=${id}`,
      transformResponse: (rawResult: { data: CaseTypeDetails }) =>
        rawResult.data,
      keepUnusedDataFor: 0,
    }),
     // --- reference tables ---
    getApplicationTypes: builder.query<ReferenceItem[], string>({
      query: () => `applicationtypes`,
      transformResponse: (r: { data: Record<string, string> }) =>
        Object.entries(r.data ?? {}).map(([code, description]) => ({ code, description })),
    }),
    getAuthorizationTypes: builder.query<ReferenceItem[], string>({
      query: () => `authorizationtypes`,
      transformResponse: (r: { data: Record<string, string> }) =>
        Object.entries(r.data ?? {}).map(([code, description]) => ({ code, description })),
    }),
    getFoodDomains: builder.query<ReferenceItem[], string>({
      query: () => `fooddomains`,
      transformResponse: (r: { data: Record<string, string> }) =>
        Object.entries(r.data ?? {}).map(([code, description]) => ({ code, description })),
    }),
    getRiskAssessmentDeadlineCodes: builder.query<RiskAssessmentDeadline[], string>({
      query: () => `riskassessmentdeadlinecodes`,
      transformResponse: (r: { data: RiskAssessmentDeadline[] }) => r.data,
    }),
    getValidationDeadlineCodes: builder.query<ValidationDeadline[], string>({
      query: () => `validationdeadlinecodes`,
      transformResponse: (r: { data: ValidationDeadline[] }) => r.data,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCaseTypeOverviewQuery,
  useGetCaseTypeDetailsQuery,
  useGetApplicationTypesQuery,
  useGetAuthorizationTypesQuery,
  useGetFoodDomainsQuery,
  useGetRiskAssessmentDeadlineCodesQuery,
  useGetValidationDeadlineCodesQuery,
} = caseTypeApi;
